import React, { useState, useEffect, useCallback } from 'react';
import { multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';
import { abrechnungAbrufen } from './api';

// ─── Module Imports ────────────────────────────────────────────────────────
import { C } from './mobile_einstellungen/SettingsTokens';
import { 
  BackIcon, 
  PersonIcon, 
  LockIcon, 
  TagIcon, 
  PaletteIcon, 
  BellIcon, 
  ShieldIcon, 
  LogoutIcon 
} from './mobile_einstellungen/SettingsIcons';
import { 
  Badge, 
  SettingsRow, 
  Section 
} from './mobile_einstellungen/SettingsComponents';
import { 
  IdentitaetSeite, 
  AccountSeite, 
  AbrechnungSeite, 
  DesignSeite, 
  BenachrichtigungenSeite, 
  DatenschutzSeite 
} from './mobile_einstellungen/SettingsPages';

export default function MobileEinstellungen({ nutzer, onZurueck, onAbmelden, themeId, onThemeChange, tonlage = 'klassisch', onTonlageChange }) {
  const [activePage, setActivePage] = useState(null);
  const [mfaAktiv, setMfaAktiv] = useState(false);
  const [abrInfo, setAbrInfo] = useState(null);

  const initiale = ((nutzer?.displayName || nutzer?.email || '?')[0]).toUpperCase();
  const anzeigename = nutzer?.displayName || nutzer?.email || '—';
  const mitgliedJahr = nutzer?.metadata?.creationTime
    ? new Date(nutzer.metadata.creationTime).getFullYear()
    : null;

  useEffect(() => {
    try {
      const enrolled = multiFactor(nutzer).enrolledFactors;
      setMfaAktiv(enrolled.some(f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID));
    } catch {}
    abrechnungAbrufen().then(r => setAbrInfo(r.data)).catch(() => {});
  }, [nutzer]);

  const aboAktiv = abrInfo?.abo_aktiv ?? false;

  const nav = useCallback((page) => setActivePage(page), []);
  const back = useCallback(() => setActivePage(null), []);

  const PAGES = {
    identitaet:      <IdentitaetSeite nutzer={nutzer} onBack={back} />,
    account:         <AccountSeite nutzer={nutzer} onBack={back} />,
    abrechnung:      <AbrechnungSeite nutzer={nutzer} onBack={back} />,
    design:          <DesignSeite onBack={back} tonlage={tonlage} onTonlageChange={onTonlageChange} themeId={themeId} onThemeChange={onThemeChange} />,
    benachrichtigungen: <BenachrichtigungenSeite onBack={back} />,
    datenschutz:     <DatenschutzSeite onBack={back} />,
  };

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', fontFamily: C.font }}>

      {/* ── Main Menu ─────────────────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', inset: 0,
        overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        background: C.bgGrad,
        transform: activePage ? 'translateX(-18%)' : 'translateX(0)',
        opacity: activePage ? 0 : 1,
        transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.20s',
        pointerEvents: activePage ? 'none' : 'auto',
      }}>

        {/* Header */}
        <div style={{ padding: '0 20px 16px', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
          <button
            onClick={onZurueck}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: C.tealSoft, border: `1px solid ${C.border}`,
              borderRadius: 999, padding: '7px 14px 7px 10px',
              cursor: 'pointer', fontFamily: C.font,
              fontSize: 14, fontWeight: 500, color: C.teal,
              marginBottom: 18, WebkitTapHighlightColor: 'transparent',
            }}
          >
            <BackIcon /> Zurück
          </button>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -0.8, lineHeight: 1.1, marginBottom: 3 }}>Einstellungen</div>
          <div style={{ fontSize: 13, color: C.textMuted }}>Konto &amp; Abrechnung</div>
        </div>

        {/* Profile card */}
        <div style={{ margin: '0 16px 24px', background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 20, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: C.shadowMd }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 50, height: 50, borderRadius: 15, background: `linear-gradient(135deg, ${C.teal}, #006A57)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#FBF7ED', boxShadow: `0 4px 14px rgba(0,77,64,0.28)` }}>{initiale}</div>
            <div style={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: 999, background: C.amber, border: `2px solid ${C.surfaceAlt}` }}/>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, letterSpacing: -0.3 }}>{anzeigename}</div>
            {mitgliedJahr && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Mitglied seit {mitgliedJahr}</div>}
          </div>
        </div>

        <Section title="Konto">
          <SettingsRow icon={<PersonIcon />} label="Identität" subtitle="Vorname, Nachname, Spitzname" onPress={() => nav('identitaet')} />
          <SettingsRow icon={<LockIcon />} label="Account-Verifizierung" subtitle="E-Mail · Passwort · 2FA" badge={<Badge label={mfaAktiv ? '2FA AKTIV' : '2FA INAKTIV'} color={mfaAktiv ? C.green : C.orange} bg={mfaAktiv ? C.greenSoft : C.orangeSoft} />} onPress={() => nav('account')} />
          <SettingsRow icon={<TagIcon />} label="Abrechnung" subtitle="Abonnement verwalten" badge={aboAktiv ? (<Badge label="AKTIV" color={C.green} bg={C.greenSoft} />) : null} onPress={() => nav('abrechnung')} last />
        </Section>

        <Section title="Sonstiges">
          <SettingsRow icon={<PaletteIcon />} label="Design & Schreibstil" subtitle="Farbschema und KI-Tonlage" onPress={() => nav('design')} />
          <SettingsRow icon={<BellIcon />} label="Benachrichtigungen" onPress={() => nav('benachrichtigungen')} />
          <SettingsRow icon={<ShieldIcon />} label="Datenschutz" subtitle="Rechtliches & Transparenz" onPress={() => nav('datenschutz')} last />
        </Section>

        <div style={{ margin: '0 16px', marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)' }}>
          <div style={{ background: C.surfaceAlt, borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: C.shadow }}>
            <button
              onClick={onAbmelden}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: C.font, fontSize: 15, fontWeight: 500, color: C.red, WebkitTapHighlightColor: 'transparent' }}
              onTouchStart={e => e.currentTarget.style.background = C.redSoft}
              onTouchEnd={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: C.redSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.red }}><LogoutIcon /></div>
              Abmelden
            </button>
          </div>
        </div>
      </div>

      {Object.entries(PAGES).map(([key, content]) => (
        <div
          key={key}
          style={{
            position: 'absolute', inset: 0,
            transform: activePage === key ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
            pointerEvents: activePage === key ? 'auto' : 'none',
            willChange: 'transform',
          }}
        >{content}</div>
      ))}
    </div>
  );
}
